/*eslint-disable*/
import { useContext, useEffect, useMemo } from 'react';
import { ClientSelectionContext } from './ClientSelectionContext';
import { useDispatch, useSelector } from 'react-redux';
import { appDataFetch } from '../redux/app/actionCreator';
import { fetchUsers } from '../redux/users/actionCreator';
import { fetchUserDetails } from '../redux/users/userDetails/actionCreator';

const getSafeId = (idData) => {
  if (!idData) return null;
  if (typeof idData === 'string') return idData;
  if (typeof idData === 'object' && idData._id) return String(idData._id);
  if (typeof idData === 'object' && idData.id) return String(idData.id);
  return String(idData);
};

export const useAppDataContext = (explicitSelectedUser = null, options = {}) => {
  const clientScope = useContext(ClientSelectionContext);
  const selectedUserId = explicitSelectedUser ?? clientScope?.selectedUser ?? null;
  const requireSelection = Boolean(options.requireSelection || clientScope?.requireSelection);
  const dispatch = useDispatch();

  const { auth, appData, usersList, headerSearchUsers, userDetails, loading } = useSelector((state) => ({
    auth: state.auth.login.user || {},
    appData: state.apps.app || [],
    usersList: state.users.users || [],
    headerSearchUsers: state.headerSearch?.users || [],
    userDetails: state.userDetails.user,
    loading: state.apps.loading || state.users.loading || state.userDetails.loading,
  }));

  useEffect(() => {
    if (!appData || appData.length === 0) {
      dispatch(appDataFetch());
    }

    if (
      (auth.role === 'Client' || auth.role === 'Assistant') &&
      (!userDetails || getSafeId(userDetails) !== getSafeId(auth) || (auth.role === 'Assistant' && !Object.prototype.hasOwnProperty.call(userDetails, 'workspace')))
    ) {
      dispatch(fetchUserDetails(auth.id));
    }

    if (auth.role === 'Admin' && usersList.length === 0) {
      dispatch(fetchUsers({ page: 1, limit: 10000 }));
    }
  }, [dispatch, auth.role, auth.id]);

  const allUsers = useMemo(() => {
    const list1 = Array.isArray(usersList) ? usersList : usersList?.users || [];
    const list2 = Array.isArray(headerSearchUsers) ? headerSearchUsers : headerSearchUsers?.users || [];
    return [...list1, ...list2];
  }, [usersList, headerSearchUsers]);

  const effectiveUser = useMemo(() => {
    if (!auth || !auth.id) return null;

    if (auth.role === 'Admin') {
      if (selectedUserId && typeof selectedUserId === 'object' && selectedUserId._id) {
        return selectedUserId;
      }

      const targetId = getSafeId(selectedUserId);
      if (targetId) {
        return allUsers.find((u) => getSafeId(u) === targetId) || (requireSelection ? null : auth);
      }

      return requireSelection ? null : auth;
    }

    if (auth.role === 'Client') {
      if (userDetails && getSafeId(userDetails) === String(auth.id)) {
        return userDetails;
      }
      return auth;
    }

    // Actor permissions/notifications belong to the assistant; usage belongs to its workspace.
    if (auth.role === 'Assistant') {
      const self = userDetails && getSafeId(userDetails) === getSafeId(auth) ? userDetails : auth;
      const memberships = [getSafeId(self.createdBy || auth.createdBy), ...(self.assignedClients || []).map(getSafeId)].filter(Boolean);
      const workspace = self.workspace && memberships.includes(getSafeId(self.workspace)) ? self.workspace : null;
      const ownerId = getSafeId(workspace) || getSafeId(self.createdBy || auth.createdBy);
      if (!ownerId) return auth;
      const parent = workspace || allUsers.find(user => getSafeId(user) === ownerId);
      return {
        ...(parent || {}),
        _id: ownerId, id: ownerId,
        app_id: parent?.app_id || self.app_id || auth.appId || auth.app_id || null,
        subscription: parent?.subscription || null,
        name: self.name || auth.name || auth.email?.split('@')[0] || 'Assistant User',
        email: self.email || auth.email,
        permissions: self.permissions || auth.permissions || {},
        supportRequests: self.supportRequests || [],
        role: 'Client', roles: 'Assistant', isAssistantMode: true, actorId: auth.id,
        createdBy: self.createdBy || auth.createdBy,
      };
    }

    return auth;
  }, [auth, allUsers, userDetails, selectedUserId, requireSelection]);

  const activeApp = useMemo(() => {
    if (!effectiveUser) return null;

    const targetId = getSafeId(effectiveUser);
    const appList = Array.isArray(appData) ? appData : appData?.apps || appData?.data || [];

    const foundApp = appList.find(
      (app) =>
        getSafeId(app.userId) === targetId ||
        getSafeId(app.user_id) === targetId ||
        getSafeId(app._id) === getSafeId(effectiveUser.app_id) ||
        getSafeId(app.app_id) === getSafeId(effectiveUser.app_id),
    );

    if (foundApp) return foundApp;

    if (effectiveUser.app_id) {
      return {
        _id: effectiveUser.app_id,
        app_id: effectiveUser.app_id,
        company: effectiveUser.name || 'Company',
        email: effectiveUser.email || '',
        image: effectiveUser.image || null,
      };
    }

    return null;
  }, [
    appData,
    effectiveUser?._id,
    effectiveUser?.app_id,
    effectiveUser?.name,
    effectiveUser?.email,
    effectiveUser?.image,
  ]);

  return {
    auth,
    usersList: allUsers,
    effectiveUser,
    activeApp,
    isAdmin: auth.role === 'Admin',
    isLoading: loading,
  };
};